import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  CircularProgress,
} from '@mui/material';
import { siteLanguagesService, getLanguageName } from 'components/api/siteLanguages';
import SupportedLanguageSelect from 'components/common/SupportedLanguageSelect/SupportedLanguageSelect';
import { SiteLanguage } from 'types/siteLanguage';
import { showToast } from 'store/toastSlice';
import { useAppDispatch } from 'store/hooks';
import styles from './LangTab.module.scss';

interface EditLanguageModalProps {
  open: boolean;
  language: SiteLanguage | null;
  onClose: () => void;
  onSuccess: () => void;
}

const EditLanguageModal: React.FC<EditLanguageModalProps> = ({
  open,
  language,
  onClose,
  onSuccess,
}) => {
  const dispatch = useAppDispatch();
  const [code, setCode] = useState('');
  const [isCustomCode, setIsCustomCode] = useState(false);
  const [name, setName] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open && language) {
      const initialCode = language.code || '';
      setCode(initialCode);
      setName(language.name || '');
      setIsCustomCode(false);

      // Fetch fresh details via GET /admin/site-languages/{id}
      const fetchFresh = async () => {
        try {
          setLoading(true);
          const fresh = await siteLanguagesService.getSiteLanguageById(language.id);
          if (fresh) {
            const freshCode = fresh.code || initialCode;
            setCode(freshCode);
            setName(fresh.name || language.name || '');
          }
        } catch (error) {
          console.warn('Could not fetch single language details, using table data:', error);
        } finally {
          setLoading(false);
        }
      };

      fetchFresh();
    }
  }, [open, language]);

  const handleSave = async () => {
    if (!language) return;

    const finalCode = code.trim().toLowerCase();
    if (!finalCode) {
      dispatch(showToast({ text: 'Language code cannot be empty', type: 'error' }));
      return;
    }

    try {
      setSaving(true);
      await siteLanguagesService.updateSiteLanguage(language.id, {
        code: finalCode,
        name: name.trim() || getLanguageName(finalCode),
      });

      dispatch(showToast({ text: 'Site language updated successfully', type: 'success' }));
      onSuccess();
      onClose();
    } catch (error: unknown) {
      console.error('Failed to update site language:', error);
      let msg = 'Failed to update site language';
      if (axios.isAxiosError(error)) {
        msg = error.response?.data?.message || error.message || msg;
      } else if (error instanceof Error) {
        msg = error.message;
      }
      dispatch(showToast({ text: msg, type: 'error' }));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={saving ? undefined : onClose}
      classes={{ paper: styles.dialogPaper }}
      maxWidth="xs"
      fullWidth
      disableRestoreFocus
    >
      <DialogTitle className={styles.dialogTitle}>
        Edit Language ({language?.code?.toUpperCase()})
      </DialogTitle>
      <DialogContent className={styles.dialogContent}>
        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: 24 }}>
            <CircularProgress size={24} />
          </div>
        ) : (
          <>
            <div className={styles.formGroup}>
              <SupportedLanguageSelect
                value={isCustomCode ? '__custom__' : code}
                onChange={(val, selectedLang) => {
                  if (val === '__custom__') {
                    setIsCustomCode(true);
                  } else {
                    setIsCustomCode(false);
                    setCode(val);
                    if (selectedLang) {
                      setName(selectedLang.name || getLanguageName(val));
                    }
                  }
                }}
                disabled={saving}
              />
            </div>

            {isCustomCode && (
              <div className={styles.formGroup}>
                <label>Custom Language Code</label>
                <input
                  type="text"
                  className={styles.formInput}
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  placeholder="e.g. en"
                  disabled={saving}
                />
              </div>
            )}

            <div className={styles.formGroup}>
              <label>Display Name</label>
              <input
                type="text"
                className={styles.formInput}
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. English"
                disabled={saving}
              />
            </div>
          </>
        )}
      </DialogContent>

      <DialogActions className={styles.dialogActions}>
        <Button
          onClick={onClose}
          disabled={saving}
          sx={{ color: 'var(--text-secondary)', textTransform: 'none' }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={saving || loading || !code.trim()}
          className={styles.addBtn}
        >
          {saving ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <CircularProgress size={16} sx={{ color: '#fff' }} />
              Saving...
            </div>
          ) : (
            'Save Changes'
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditLanguageModal;
